import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Users, Plus, Search, Star, MapPin, Mail, Phone, 
  MoreVertical, Eye, Edit2, Trash2, X, ChevronDown,
  Briefcase, Clock, DollarSign, Upload, FileText, Image
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';

const API_URL = window.location.origin;

const TalentDatabase = () => {
  const userId = useUserId();
  
  // State
  const [talents, setTalents] = useState([]);
  const [stats, setStats] = useState({ total_talent: 0, available_now: 0, active_projects: 0, average_rating: 0 });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [type, setType] = useState('');
  const [industry, setIndustry] = useState('');
  const [role, setRole] = useState('');
  const [availability, setAvailability] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  
  // Upload state
  const [uploadProgress, setUploadProgress] = useState(null);
  const [parsedTalents, setParsedTalents] = useState(null);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    skills: [],
    availability: 'Available',
    rate: '',
    location: '',
    email: '',
    phone: '',
    bio: '',
    type: 'Personal',
    industry: 'Corporate'
  });
  const [skillInput, setSkillInput] = useState('');

  const types = ['Personal', 'Corporate', 'Agency'];
  const industries = ['Corporate', 'Entertainment', 'Media', 'Music', 'Fashion', 'Sports', 'Technology'];
  const availabilities = ['Available', 'Busy', 'Unavailable'];

  // Fetch data
  const fetchTalents = useCallback(async () => {
    if (!userId) return;
    
    try {
      const params = new URLSearchParams({ user_id: userId });
      if (type) params.append('type', type);
      if (industry) params.append('industry', industry);
      if (role) params.append('role', role);
      if (availability) params.append('availability', availability);
      if (searchQuery) params.append('search', searchQuery);
      params.append('sort_by', sortBy);
      
      const res = await fetch(`${API_URL}/api/talent/?${params}`);
      const data = await res.json();
      setTalents(data);
    } catch (error) {
      console.error('Error fetching talents:', error);
    }
  }, [userId, type, industry, role, availability, searchQuery, sortBy]);

  const fetchStats = useCallback(async () => {
    if (!userId) return;
    
    try {
      const params = new URLSearchParams({ user_id: userId });
      if (type) params.append('type', type);
      if (industry) params.append('industry', industry);
      if (role) params.append('role', role);
      
      const res = await fetch(`${API_URL}/api/talent/stats?${params}`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [userId, type, industry, role]);

  const fetchRoles = useCallback(async () => {
    if (!userId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/talent/roles?user_id=${userId}`);
      const data = await res.json();
      setRoles(data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  }, [userId]);

  // Seed data if empty
  const seedData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/talent/seed?user_id=${userId}`, { method: 'POST' });
      const data = await res.json();
      if (data.seeded) {
        toast.success('Sample talent data loaded');
        fetchTalents();
        fetchStats();
        fetchRoles();
      }
    } catch (error) {
      console.error('Error seeding data:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTalents(), fetchStats(), fetchRoles()]);
      setLoading(false);
    };
    
    if (userId) {
      loadData();
    }
  }, [userId, fetchTalents, fetchStats, fetchRoles]);

  // Auto-seed if no data
  useEffect(() => {
    if (!loading && talents.length === 0 && userId) {
      seedData();
    }
  }, [loading, talents.length, userId]);

  // CRUD operations
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.role) {
      toast.error('Name and role are required');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/talent/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          rate: formData.rate ? parseFloat(formData.rate) : null,
          user_id: userId
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Talent added successfully');
        setShowAddModal(false);
        resetForm();
        fetchTalents();
        fetchStats();
        fetchRoles();
      }
    } catch (error) {
      toast.error('Failed to add talent');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedTalent) return;

    try {
      const res = await fetch(`${API_URL}/api/talent/${selectedTalent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          rate: formData.rate ? parseFloat(formData.rate) : null
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Talent updated successfully');
        setShowEditModal(false);
        setSelectedTalent(null);
        resetForm();
        fetchTalents();
        fetchStats();
        fetchRoles();
      }
    } catch (error) {
      toast.error('Failed to update talent');
    }
  };

  const handleDelete = async (talentId) => {
    if (!window.confirm('Are you sure you want to delete this talent?')) return;

    try {
      const res = await fetch(`${API_URL}/api/talent/${talentId}`, { method: 'DELETE' });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Talent deleted');
        fetchTalents();
        fetchStats();
        fetchRoles();
      }
    } catch (error) {
      toast.error('Failed to delete talent');
    }
  };

  // File upload handling
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'text/csv'];
    const isCSV = file.name.endsWith('.csv');
    
    if (!allowedTypes.includes(file.type) && !isCSV) {
      toast.error('Unsupported file type. Please upload PDF, CSV, JPG, PNG, or WebP files.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploadProgress('reading');
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        setUploadProgress('parsing');
        
        try {
          const response = await fetch(`${API_URL}/api/talent/parse-document`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              file_base64: base64,
              file_type: isCSV ? 'text/csv' : file.type,
              file_name: file.name,
              user_id: userId
            })
          });

          const result = await response.json();
          
          if (result.success && result.talents?.length > 0) {
            setParsedTalents(result.talents);
            setUploadProgress(null);
            toast.success(`Found ${result.talents.length} talent(s) in document`);
          } else {
            setUploadProgress(null);
            toast.error(result.message || 'No talent data found in document');
          }
        } catch (error) {
          setUploadProgress(null);
          toast.error('Failed to parse document');
          console.error('Parse error:', error);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadProgress(null);
      toast.error('Failed to read file');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportParsedTalents = async () => {
    if (!parsedTalents?.length) return;
    
    setUploadProgress('importing');
    
    try {
      const response = await fetch(`${API_URL}/api/talent/import-parsed?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsedTalents)
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Imported ${result.imported_count} talent(s)`);
        setParsedTalents(null);
        setShowUploadModal(false);
        fetchTalents();
        fetchStats();
        fetchRoles();
      } else {
        toast.error('Failed to import talents');
      }
    } catch (error) {
      toast.error('Failed to import talents');
    }
    
    setUploadProgress(null);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setParsedTalents(null);
    setUploadProgress(null);
  };

  const handleHire = async (talent) => {
    try {
      const res = await fetch(`${API_URL}/api/talent/${talent.id}/hire`, { method: 'POST' });
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        fetchTalents();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to hire talent');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      skills: [],
      availability: 'Available',
      rate: '',
      location: '',
      email: '',
      phone: '',
      bio: '',
      type: 'Personal',
      industry: 'Corporate'
    });
    setSkillInput('');
  };

  const openEdit = (talent) => {
    setSelectedTalent(talent);
    setFormData({
      name: talent.name || '',
      role: talent.role || '',
      skills: talent.skills || [],
      availability: talent.availability || 'Available',
      rate: talent.rate || '',
      location: talent.location || '',
      email: talent.email || '',
      phone: talent.phone || '',
      bio: talent.bio || '',
      type: talent.type || 'Personal',
      industry: talent.industry || 'Corporate'
    });
    setShowEditModal(true);
    setActiveMenu(null);
  };

  const openView = (talent) => {
    setSelectedTalent(talent);
    setShowViewModal(true);
    setActiveMenu(null);
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, skillInput.trim()] });
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });
  };

  const getAvailabilityColor = (avail) => {
    switch (avail) {
      case 'Available': return 'bg-green-500';
      case 'Busy': return 'bg-yellow-500';
      case 'Unavailable': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading talent database...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Talent Database</h1>
            <p className="text-gray-400 mt-1">Manage and discover creative talent</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Upload Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-black border border-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
              data-testid="upload-talent-btn"
            >
              <Upload size={20} />
              Upload
            </button>
            
            <button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="add-talent-btn"
            >
              <Plus size={20} />
              Add Talent
            </button>
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative">
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="appearance-none bg-black border border-gray-700 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="">Type: All</option>
              {types.map(t => <option key={t} value={t}>Type: {t}</option>)}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          
          <div className="relative">
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="appearance-none bg-black border border-gray-700 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="">Industry: All</option>
              {industries.map(i => <option key={i} value={i}>Industry: {i}</option>)}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          
          <div className="relative">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="appearance-none bg-black border border-gray-700 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="">Role: All</option>
              {roles.map(r => <option key={r} value={r}>Role: {r}</option>)}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <p className="text-gray-400 text-sm">Total Talent</p>
            <p className="text-2xl font-bold text-white">{stats.total_talent}</p>
          </div>
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <p className="text-gray-400 text-sm">Available Now</p>
            <p className="text-2xl font-bold text-green-400">{stats.available_now}</p>
          </div>
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <p className="text-gray-400 text-sm">Active Projects</p>
            <p className="text-2xl font-bold text-white">{stats.active_projects}</p>
          </div>
          <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
            <p className="text-gray-400 text-sm">Average Rating</p>
            <p className="text-2xl font-bold text-white flex items-center gap-1">
              <Star size={20} className="text-yellow-400 fill-yellow-400" />
              {stats.average_rating}
            </p>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, skill, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>
            
            <div className="flex gap-3">
              <div className="relative">
                <select
                  value={availability}
                  onChange={(e) => setAvailability(e.target.value)}
                  className="appearance-none bg-black border border-gray-700 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-blue-500 min-w-[150px]"
                >
                  <option value="">All Availability</option>
                  {availabilities.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
              
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-black border border-gray-700 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-blue-500 min-w-[150px]"
                >
                  <option value="name">Name (A-Z)</option>
                  <option value="rating">Highest Rating</option>
                  <option value="projects">Most Projects</option>
                  <option value="rate">Highest Rate</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Talent Grid */}
        {talents.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Users size={48} className="mx-auto mb-4 opacity-50" />
            <p>No talent found. Click "Add Talent" to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {talents.map(talent => (
              <div 
                key={talent.id} 
                className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4 hover:border-gray-300 transition-colors relative"
                data-testid={`talent-card-${talent.id}`}
              >
                {/* Menu Button */}
                <div className="absolute top-3 right-3">
                  <button
                    onClick={() => setActiveMenu(activeMenu === talent.id ? null : talent.id)}
                    className="p-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded"
                  >
                    <MoreVertical size={18} />
                  </button>
                  
                  {activeMenu === talent.id && (
                    <div className="absolute right-0 top-8 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-10 min-w-[120px]">
                      <button
                        onClick={() => openView(talent)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-t-lg"
                      >
                        <Eye size={14} /> View
                      </button>
                      <button
                        onClick={() => openEdit(talent)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-800"
                      >
                        <Edit2 size={14} /> Edit
                      </button>
                      <button
                        onClick={() => { handleDelete(talent.id); setActiveMenu(null); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-gray-800 rounded-b-lg"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  )}
                </div>

                {/* Avatar */}
                <div className="flex flex-col items-center mb-4">
                  <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center text-2xl font-bold text-white mb-3">
                    {talent.name?.charAt(0)?.toUpperCase() || 'T'}
                  </div>
                  <h3 className="text-white font-semibold text-center">{talent.name}</h3>
                  <p className="text-gray-400 text-sm">{talent.role}</p>
                </div>

                {/* Availability Badge */}
                <div className="flex justify-center mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getAvailabilityColor(talent.availability)}`}>
                    {talent.availability}
                  </span>
                </div>

                {/* Rating & Projects */}
                <div className="flex justify-center items-center gap-4 mb-3 text-sm">
                  <span className="flex items-center gap-1 text-yellow-400">
                    <Star size={14} className="fill-yellow-400" />
                    {talent.rating || 5}
                  </span>
                  <span className="text-gray-400">{talent.projects || 0} projects</span>
                </div>

                {/* Skills */}
                {talent.skills && talent.skills.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1 mb-3">
                    {talent.skills.slice(0, 3).map(skill => (
                      <span key={skill} className="px-2 py-0.5 bg-gray-800 text-gray-300 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                    {talent.skills.length > 3 && (
                      <span className="text-gray-500 text-xs">+{talent.skills.length - 3}</span>
                    )}
                  </div>
                )}

                {/* Location & Email */}
                <div className="space-y-1 mb-4 text-sm">
                  {talent.location && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <MapPin size={14} />
                      <span className="truncate">{talent.location}</span>
                    </div>
                  )}
                  {talent.email && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Mail size={14} />
                      <span className="truncate">{talent.email}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleHire(talent)}
                    disabled={talent.availability === 'Unavailable'}
                    className="flex-1 py-2 bg-black border border-gray-700 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Hire
                  </button>
                  <button
                    onClick={() => openView(talent)}
                    className="p-2 bg-black border border-gray-700 text-gray-400 rounded-lg hover:text-white hover:bg-gray-900 transition-colors"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => openEdit(talent)}
                    className="p-2 bg-black border border-gray-700 text-gray-400 rounded-lg hover:text-white hover:bg-gray-900 transition-colors"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(talent.id)}
                    className="p-2 bg-black border border-gray-700 text-gray-400 rounded-lg hover:text-red-400 hover:bg-gray-900 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        {(showAddModal || showEditModal) && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">
                  {showEditModal ? 'Edit Talent' : 'Add New Talent'}
                </h2>
                <button 
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); setSelectedTalent(null); resetForm(); }}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>
              
              <form onSubmit={showEditModal ? handleUpdate : handleCreate} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-400 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Role *</label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      placeholder="e.g., Videographer"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Availability</label>
                    <select
                      value={formData.availability}
                      onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                      {availabilities.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                      {types.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Industry</label>
                    <select
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                      {industries.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Rate ($/hr)</label>
                    <input
                      type="number"
                      value={formData.rate}
                      onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      placeholder="150"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      placeholder="Dallas, Texas"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-400 mb-1">Skills</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={skillInput}
                        onChange={(e) => setSkillInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                        className="flex-1 px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                        placeholder="Add skill and press Enter"
                      />
                      <button type="button" onClick={addSkill} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.skills.map(skill => (
                        <span key={skill} className="flex items-center gap-1 px-2 py-1 bg-gray-800 text-gray-300 rounded text-sm">
                          {skill}
                          <button type="button" onClick={() => removeSkill(skill)} className="hover:text-red-400">
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-400 mb-1">Bio</label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    {showEditModal ? 'Update' : 'Add Talent'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddModal(false); setShowEditModal(false); setSelectedTalent(null); resetForm(); }}
                    className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedTalent && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md">
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Talent Profile</h2>
                <button onClick={() => { setShowViewModal(false); setSelectedTalent(null); }} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6">
                <div className="flex flex-col items-center mb-6">
                  <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center text-3xl font-bold text-white mb-3">
                    {selectedTalent.name?.charAt(0)?.toUpperCase() || 'T'}
                  </div>
                  <h3 className="text-xl font-semibold text-white">{selectedTalent.name}</h3>
                  <p className="text-gray-400">{selectedTalent.role}</p>
                  <span className={`mt-2 px-3 py-1 rounded-full text-xs font-medium text-white ${getAvailabilityColor(selectedTalent.availability)}`}>
                    {selectedTalent.availability}
                  </span>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Star size={18} className="text-yellow-400" />
                    <span>{selectedTalent.rating || 5} Rating</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Briefcase size={18} className="text-gray-400" />
                    <span>{selectedTalent.projects || 0} Projects</span>
                  </div>
                  {selectedTalent.rate && (
                    <div className="flex items-center gap-3 text-gray-300">
                      <DollarSign size={18} className="text-green-400" />
                      <span>${selectedTalent.rate}/hr</span>
                    </div>
                  )}
                  {selectedTalent.location && (
                    <div className="flex items-center gap-3 text-gray-300">
                      <MapPin size={18} className="text-gray-400" />
                      <span>{selectedTalent.location}</span>
                    </div>
                  )}
                  {selectedTalent.email && (
                    <div className="flex items-center gap-3 text-gray-300">
                      <Mail size={18} className="text-gray-400" />
                      <span>{selectedTalent.email}</span>
                    </div>
                  )}
                  {selectedTalent.phone && (
                    <div className="flex items-center gap-3 text-gray-300">
                      <Phone size={18} className="text-gray-400" />
                      <span>{selectedTalent.phone}</span>
                    </div>
                  )}
                </div>
                
                {selectedTalent.skills && selectedTalent.skills.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-400 mb-2">Skills</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedTalent.skills.map(skill => (
                        <span key={skill} className="px-2 py-1 bg-gray-800 text-gray-300 rounded text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedTalent.bio && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-400 mb-2">Bio</p>
                    <p className="text-gray-300">{selectedTalent.bio}</p>
                  </div>
                )}
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => handleHire(selectedTalent)}
                    disabled={selectedTalent.availability === 'Unavailable'}
                    className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Hire
                  </button>
                  <button
                    onClick={() => { setShowViewModal(false); openEdit(selectedTalent); }}
                    className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Click outside to close menu */}
      {activeMenu && (
        <div className="fixed inset-0 z-0" onClick={() => setActiveMenu(null)} />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={handleCloseUploadModal}
          data-testid="upload-talent-modal"
        >
          <div 
            className="bg-black border border-gray-400 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <h3 className="text-white text-lg font-semibold">Upload Talent Data</h3>
              <button 
                onClick={handleCloseUploadModal}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="px-6 py-5 space-y-5">
              {/* Upload Area */}
              {!parsedTalents && (
                <div className="space-y-4">
                  <p className="text-gray-400 text-sm">
                    Upload a PDF, CSV, or image file containing talent information. The system will automatically extract contact and profile data.
                  </p>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".pdf,.csv,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    data-testid="talent-file-input"
                  />
                  
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center cursor-pointer hover:border-gray-500 transition-colors"
                  >
                    {uploadProgress ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <p className="text-white">
                          {uploadProgress === 'reading' ? 'Reading file...' : 
                           uploadProgress === 'parsing' ? 'Extracting talent data with AI...' : 
                           'Processing...'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload size={40} className="mx-auto text-gray-500 mb-3" />
                        <p className="text-white font-medium">Click to upload or drag and drop</p>
                        <p className="text-gray-500 text-sm mt-1">PDF, CSV, JPG, PNG, WebP (max 10MB)</p>
                      </>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <FileText size={16} />
                      <span>CSV talent lists</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Image size={16} />
                      <span>Business cards, portfolios</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Parsed Data Preview */}
              {parsedTalents && parsedTalents.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-white font-medium">Found {parsedTalents.length} Talent(s)</h4>
                    <button
                      onClick={() => { setParsedTalents(null); }}
                      className="text-gray-400 hover:text-white text-sm"
                    >
                      Upload different file
                    </button>
                  </div>
                  
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {parsedTalents.map((talent, idx) => (
                      <div key={idx} className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">{talent.name || 'Unknown'}</p>
                            <p className="text-gray-400 text-sm">{talent.role || 'No role specified'}</p>
                            {talent.location && (
                              <p className="text-gray-500 text-xs flex items-center gap-1 mt-1">
                                <MapPin size={12} />
                                {talent.location}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            {talent.rate > 0 && (
                              <p className="text-white font-medium">${talent.rate}/hr</p>
                            )}
                            <p className="text-gray-500 text-xs">{talent.availability || 'Available'}</p>
                          </div>
                        </div>
                        {talent.skills?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {talent.skills.slice(0, 4).map((skill, sIdx) => (
                              <span key={sIdx} className="px-2 py-0.5 bg-gray-800 text-gray-300 text-xs rounded">
                                {skill}
                              </span>
                            ))}
                            {talent.skills.length > 4 && (
                              <span className="px-2 py-0.5 text-gray-500 text-xs">
                                +{talent.skills.length - 4} more
                              </span>
                            )}
                          </div>
                        )}
                        {(talent.email || talent.phone) && (
                          <div className="mt-2 pt-2 border-t border-gray-800 text-gray-400 text-xs space-y-1">
                            {talent.email && <p>{talent.email}</p>}
                            {talent.phone && <p>{talent.phone}</p>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                    <button
                      onClick={handleCloseUploadModal}
                      className="px-4 py-2 text-gray-300 hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleImportParsedTalents}
                      disabled={uploadProgress === 'importing'}
                      className="px-5 py-2 bg-white text-black rounded-lg font-medium hover:bg-gray-100 disabled:opacity-50"
                      data-testid="import-talents-btn"
                    >
                      {uploadProgress === 'importing' ? 'Importing...' : `Import ${parsedTalents.length} Talent(s)`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentDatabase;
